import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import type {
  GetTemplateCreationRecordResponse,
  UpdateTemplateCreationRecordRequest,
} from '../../contracts';
import {
  GET_TEMPLATE_CREATION_RECORD_BY_ID,
  UPDATE_TEMPLATE_CREATION_RECORD_KEY,
} from '../../contracts';
import { updateTemplateCreationRecord } from '../api/templates';
import { invalidateTemplateCreationRecordById } from '../invalidate';

export const useMutateUpdateTemplateCreationRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [UPDATE_TEMPLATE_CREATION_RECORD_KEY],
    mutationFn: async ({
      templateCreationRecordId,
      data,
      signal,
    }: {
      templateCreationRecordId: string;
      data: UpdateTemplateCreationRecordRequest;
      signal?: AbortSignal;
    }) => {
      return await updateTemplateCreationRecord({
        templateCreationRecordId,
        data,
        signal,
      });
    },

    onMutate: ({ templateCreationRecordId, data }) => {
      queryClient.setQueryData<GetTemplateCreationRecordResponse>(
        [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          return produce(oldData, (draft) => {
            draft.template_creation_record = {
              ...draft.template_creation_record,
              ...data,
            };
          });
        }
      );
    },

    onError: (error, { templateCreationRecordId }) => {
      // sanitizeError, never the raw error: the AxiosError's config.data carries
      // the patch (template name, transcript) and the 400 body echoes the
      // backend exception text. This is the one report for a failed update, so
      // callers that run the patch in the background settle the rejection
      // without logging it again (DSK-1315).
      logger.error('[TEMPLATE_CREATION_RECORD] update failed', {
        template_creation_record_id: templateCreationRecordId,
        error: sanitizeError(error),
      });
      void invalidateTemplateCreationRecordById(templateCreationRecordId);
    },
  });
};
